import type { OcrFoliarSample, OcrSoilSample } from '@practium/features/api';
import type { PlantAnalysisFormData, SoilAnalysisFormData } from '../types/analyses.types';

const formatOcrValue = (measure?: { value: number }) =>
	measure?.value === undefined || measure.value === null ? '' : String(measure.value);

const formatOcrUnit = (measure: { unit: string } | undefined, fallback: string) =>
	measure?.unit || fallback;

export const mapOcrSampleToSoilForm = (
	currentForm: SoilAnalysisFormData,
	sample: OcrSoilSample
): SoilAnalysisFormData => ({
	...currentForm,
	name: sample.sampleLabel || sample.sampleLabId || currentForm.name,
	Pm1: formatOcrValue(sample.Pm1),
	Pres: formatOcrValue(sample.Pres),
	K: formatOcrValue(sample.K),
	Ca: formatOcrValue(sample.Ca),
	Mg: formatOcrValue(sample.Mg),
	S: formatOcrValue(sample.S),
	B: formatOcrValue(sample.B),
	Cu: formatOcrValue(sample.Cu),
	Fe: formatOcrValue(sample.Fe),
	Mn: formatOcrValue(sample.Mn),
	Zn: formatOcrValue(sample.Zn),
	pHagua: formatOcrValue(sample.pHagua),
	pHCaCl2: formatOcrValue(sample.pHCaCl2),
	Al: formatOcrValue(sample.Al),
	HAl: formatOcrValue(sample.HAl),
	MO: formatOcrValue(sample.MO),
	Na: formatOcrValue(sample.Na),
	Prem: formatOcrValue(sample.Prem),
	SB: formatOcrValue(sample.SB),
	ctcE: formatOcrValue(sample.ctcE),
	ctcT: formatOcrValue(sample.ctcT),
	CaMg: formatOcrValue(sample.CaMg),
	satSB: formatOcrValue(sample.satSB),
	satAl: formatOcrValue(sample.satAl),
	satCa: formatOcrValue(sample.satCa),
	satMg: formatOcrValue(sample.satMg),
	satK: formatOcrValue(sample.satK),
	textureMode:
		sample.Prem?.value !== undefined && sample.Prem?.value !== null
			? 'Prem'
			: currentForm.textureMode,
	units: {
		Prem: formatOcrUnit(sample.Prem, currentForm.units.Prem),
		clay: currentForm.units.clay,
		Pm1: formatOcrUnit(sample.Pm1, currentForm.units.Pm1),
		Pres: formatOcrUnit(sample.Pres, currentForm.units.Pres),
		K: formatOcrUnit(sample.K, currentForm.units.K),
		Ca: formatOcrUnit(sample.Ca, currentForm.units.Ca),
		Mg: formatOcrUnit(sample.Mg, currentForm.units.Mg),
		S: formatOcrUnit(sample.S, currentForm.units.S),
		Na: formatOcrUnit(sample.Na, currentForm.units.Na),
		B: formatOcrUnit(sample.B, currentForm.units.B),
		Cu: formatOcrUnit(sample.Cu, currentForm.units.Cu),
		Fe: formatOcrUnit(sample.Fe, currentForm.units.Fe),
		Mn: formatOcrUnit(sample.Mn, currentForm.units.Mn),
		Zn: formatOcrUnit(sample.Zn, currentForm.units.Zn),
		MO: formatOcrUnit(sample.MO, currentForm.units.MO),
		pHagua: formatOcrUnit(sample.pHagua, currentForm.units.pHagua),
		pHCaCl2: formatOcrUnit(sample.pHCaCl2, currentForm.units.pHCaCl2),
		Al: formatOcrUnit(sample.Al, currentForm.units.Al),
		HAl: formatOcrUnit(sample.HAl, currentForm.units.HAl),
		SB: formatOcrUnit(sample.SB, currentForm.units.SB),
		ctcE: formatOcrUnit(sample.ctcE, currentForm.units.ctcE),
		ctcT: formatOcrUnit(sample.ctcT, currentForm.units.ctcT),
		CaMg: formatOcrUnit(sample.CaMg, currentForm.units.CaMg),
		satSB: formatOcrUnit(sample.satSB, currentForm.units.satSB),
		satAl: formatOcrUnit(sample.satAl, currentForm.units.satAl),
		satCa: formatOcrUnit(sample.satCa, currentForm.units.satCa),
		satMg: formatOcrUnit(sample.satMg, currentForm.units.satMg),
		satK: formatOcrUnit(sample.satK, currentForm.units.satK),
	},
});

export const mapOcrSampleToPlantForm = (
	currentForm: PlantAnalysisFormData,
	sample: OcrFoliarSample
): PlantAnalysisFormData => ({
	...currentForm,
	name: sample.sampleLabel || sample.sampleLabId || currentForm.name,
	N: formatOcrValue(sample.N),
	P: formatOcrValue(sample.P),
	K: formatOcrValue(sample.K),
	Ca: formatOcrValue(sample.Ca),
	Mg: formatOcrValue(sample.Mg),
	S: formatOcrValue(sample.S),
	B: formatOcrValue(sample.B),
	Cu: formatOcrValue(sample.Cu),
	Fe: formatOcrValue(sample.Fe),
	Mn: formatOcrValue(sample.Mn),
	Zn: formatOcrValue(sample.Zn),
	units: {
		N: formatOcrUnit(sample.N, currentForm.units.N),
		P: formatOcrUnit(sample.P, currentForm.units.P),
		K: formatOcrUnit(sample.K, currentForm.units.K),
		Ca: formatOcrUnit(sample.Ca, currentForm.units.Ca),
		Mg: formatOcrUnit(sample.Mg, currentForm.units.Mg),
		S: formatOcrUnit(sample.S, currentForm.units.S),
		B: formatOcrUnit(sample.B, currentForm.units.B),
		Cu: formatOcrUnit(sample.Cu, currentForm.units.Cu),
		Fe: formatOcrUnit(sample.Fe, currentForm.units.Fe),
		Mn: formatOcrUnit(sample.Mn, currentForm.units.Mn),
		Zn: formatOcrUnit(sample.Zn, currentForm.units.Zn),
	},
});
